import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { 
  Send, 
  User, 
  Mail, 
  Phone, 
  MessageSquare,
  CheckCircle,
  MapPin,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WHATSAPP_NUMBER = '40766475946';

const contactTranslations = {
  en: {
    pageTitle: "Contact Me",
    subtitle: "I'd love to hear from you",
    description: "Have questions about LifeWave products? Want to share your story? Or just want to say hello? Fill out the form below and I'll get back to you as soon as possible.",
    nameLabel: "Your Name",
    namePlaceholder: "Enter your name",
    emailLabel: "Email Address",
    emailPlaceholder: "your@email.com",
    phoneLabel: "Phone Number",
    phonePlaceholder: "+40 xxx xxx xxx",
    messageLabel: "Your Message",
    messagePlaceholder: "Tell me about yourself, your health goals, or any questions you have about LifeWave...",
    sendButton: "Send Message",
    sending: "Sending...",
    successTitle: "Message Sent!",
    successMessage: "Thank you for reaching out. I'll get back to you within 24 hours.",
    sendAnother: "Send Another Message",
    orContact: "Or contact me directly:",
    whatsappText: "Chat on WhatsApp",
    locationTitle: "Based in",
    location: "Romania, Europe"
  },
  ro: {
    pageTitle: "Contactează-mă",
    subtitle: "Mi-ar plăcea să aud de la tine",
    description: "Ai întrebări despre produsele LifeWave? Vrei să îmi spui povestea ta? Sau doar vrei să spui bună? Completează formularul de mai jos și îți voi răspunde cât mai curând posibil.",
    nameLabel: "Numele Tău",
    namePlaceholder: "Introdu numele tău",
    emailLabel: "Adresa de Email",
    emailPlaceholder: "email@exemplu.com",
    phoneLabel: "Număr de Telefon",
    phonePlaceholder: "+40 xxx xxx xxx",
    messageLabel: "Mesajul Tău",
    messagePlaceholder: "Spune-mi despre tine, obiectivele tale de sănătate, sau orice întrebări ai despre LifeWave...",
    sendButton: "Trimite Mesajul",
    sending: "Se trimite...",
    successTitle: "Mesaj Trimis!",
    successMessage: "Mulțumesc că m-ai contactat. Îți voi răspunde în maxim 24 de ore.",
    sendAnother: "Trimite Alt Mesaj",
    orContact: "Sau contactează-mă direct:",
    whatsappText: "Scrie pe WhatsApp",
    locationTitle: "Locație",
    location: "România, Europa"
  },
  es: {
    pageTitle: "Contáctame",
    subtitle: "Me encantaría saber de ti",
    description: "¿Tienes preguntas sobre los productos LifeWave? ¿Quieres compartir tu historia? ¿O simplemente quieres saludar? Completa el formulario a continuación y te responderé lo antes posible.",
    nameLabel: "Tu Nombre",
    namePlaceholder: "Ingresa tu nombre",
    emailLabel: "Correo Electrónico",
    emailPlaceholder: "tu@correo.com",
    phoneLabel: "Número de Teléfono",
    phonePlaceholder: "+34 xxx xxx xxx",
    messageLabel: "Tu Mensaje",
    messagePlaceholder: "Cuéntame sobre ti, tus objetivos de salud, o cualquier pregunta que tengas sobre LifeWave...",
    sendButton: "Enviar Mensaje",
    sending: "Enviando...",
    successTitle: "¡Mensaje Enviado!",
    successMessage: "Gracias por contactarme. Te responderé dentro de 24 horas.",
    sendAnother: "Enviar Otro Mensaje",
    orContact: "O contáctame directamente:",
    whatsappText: "Chatear en WhatsApp",
    locationTitle: "Ubicación",
    location: "Rumania, Europa"
  },
  it: {
    pageTitle: "Contattami",
    subtitle: "Mi piacerebbe sentirti",
    description: "Hai domande sui prodotti LifeWave? Vuoi condividere la tua storia? O semplicemente vuoi salutare? Compila il modulo qui sotto e ti risponderò il prima possibile.",
    nameLabel: "Il Tuo Nome",
    namePlaceholder: "Inserisci il tuo nome",
    emailLabel: "Indirizzo Email",
    emailPlaceholder: "tua@email.com",
    phoneLabel: "Numero di Telefono",
    phonePlaceholder: "+39 xxx xxx xxx",
    messageLabel: "Il Tuo Messaggio",
    messagePlaceholder: "Raccontami di te, dei tuoi obiettivi di salute, o qualsiasi domanda tu abbia su LifeWave...",
    sendButton: "Invia Messaggio",
    sending: "Invio...",
    successTitle: "Messaggio Inviato!",
    successMessage: "Grazie per avermi contattato. Ti risponderò entro 24 ore.",
    sendAnother: "Invia Un Altro Messaggio",
    orContact: "O contattami direttamente:",
    whatsappText: "Chatta su WhatsApp",
    locationTitle: "Posizione",
    location: "Romania, Europa"
  },
  de: {
    pageTitle: "Kontaktiere Mich",
    subtitle: "Ich würde gerne von dir hören",
    description: "Hast du Fragen zu LifeWave-Produkten? Möchtest du deine Geschichte teilen? Oder einfach nur Hallo sagen? Fülle das Formular unten aus und ich werde mich so schnell wie möglich bei dir melden.",
    nameLabel: "Dein Name",
    namePlaceholder: "Gib deinen Namen ein",
    emailLabel: "E-Mail-Adresse",
    emailPlaceholder: "deine@email.com",
    phoneLabel: "Telefonnummer",
    phonePlaceholder: "+49 xxx xxx xxx",
    messageLabel: "Deine Nachricht",
    messagePlaceholder: "Erzähl mir von dir, deinen Gesundheitszielen, oder stelle mir Fragen zu LifeWave...",
    sendButton: "Nachricht Senden",
    sending: "Wird gesendet...",
    successTitle: "Nachricht Gesendet!",
    successMessage: "Danke für deine Nachricht. Ich werde dir innerhalb von 24 Stunden antworten.",
    sendAnother: "Weitere Nachricht Senden",
    orContact: "Oder kontaktiere mich direkt:",
    whatsappText: "Chat auf WhatsApp",
    locationTitle: "Standort",
    location: "Rumänien, Europa"
  }
};

const ContactPage = () => {
  const { language } = useLanguage();
  const t = contactTranslations[language] || contactTranslations['en'];
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    
    try {
      // Send to backend API
      await axios.post(`${API_URL}/api/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message
      });
      
      setSent(true);
    } catch (err) {
      console.error('Contact form error:', err);
      setError(language === 'ro' 
        ? 'A apărut o eroare. Te rog încearcă din nou sau contactează-mă pe WhatsApp.'
        : 'An error occurred. Please try again or contact me via WhatsApp.');
    } finally {
      setSending(false);
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}`, '_blank');
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-slate-50 py-20" data-testid="contact-success">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{t.successTitle}</h2>
            <p className="text-slate-600 mb-6">{t.successMessage}</p>
            <Button 
              onClick={() => { setSent(false); setError(null); setFormData({ name: '', email: '', phone: '', message: '' }); }}
              className="bg-sky-600 hover:bg-sky-700 text-white rounded-full"
              data-testid="send-another-btn"
            >
              {t.sendAnother}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-16" data-testid="contact-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">{t.pageTitle}</h1>
          <p className="text-xl text-slate-600 mb-2">{t.subtitle}</p>
          <p className="text-slate-500 max-w-2xl mx-auto">{t.description}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm">
              <div className="space-y-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    {t.nameLabel}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t.namePlaceholder}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                    data-testid="contact-name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    {t.emailLabel}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t.emailPlaceholder}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                    data-testid="contact-email"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    {t.phoneLabel}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t.phonePlaceholder}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                    data-testid="contact-phone"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <MessageSquare className="w-4 h-4 inline mr-2" />
                    {t.messageLabel}
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t.messagePlaceholder}
                    required
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 resize-none"
                    data-testid="contact-message"
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700" data-testid="contact-error">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-full py-6 text-lg"
                  data-testid="contact-submit"
                >
                  {sending ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t.sending}
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Send className="w-5 h-5" />
                      {t.sendButton}
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </div>

          {/* Contact Info Sidebar */}
          <div className="space-y-6">
            {/* WhatsApp Card */}
            <div className="bg-green-50 rounded-2xl p-6 border border-green-100">
              <h3 className="font-semibold text-green-800 mb-2">{t.orContact}</h3>
              <Button
                onClick={handleWhatsApp}
                className="w-full bg-green-500 hover:bg-green-600 text-white rounded-full"
              >
                <Phone className="w-5 h-5 mr-2" />
                {t.whatsappText}
              </Button>
              <p className="text-green-700 text-sm mt-3 text-center">
                +40 766 475 946
              </p>
            </div>

            {/* Location Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t.locationTitle}</p>
                  <p className="font-semibold text-slate-900">{t.location}</p>
                </div>
              </div>
            </div>

            {/* Ioana Info */}
            <div className="bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold">IA</span>
                </div>
                <div>
                  <p className="font-bold">Ioana Alexandra</p>
                  <p className="text-sky-100 text-sm">LifeWave Partner</p>
                </div>
              </div>
              <p className="text-sky-100 text-sm">
                {language === 'ro' 
                  ? 'Sunt aici să te ajut să descoperi beneficiile LifeWave și să îți răspund la orice întrebare.'
                  : 'I\'m here to help you discover the benefits of LifeWave and answer any questions you may have.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
